import { MDText } from 'i18n-react';

import { LoadingIndicator } from '@motorway/mw-highway-code/alpha';

import { SpecSection } from 'Components/SpecSection/SpecSection';
import { useVehicleData } from 'Context/VehicleContext/VehicleDataContext';
import type { User } from 'Types/user';
import useFeatureToggle, { FEATURES } from 'Utilities/hooks/useFeatureToggle';
import useUser from 'Utilities/hooks/useUser';

import { useTransportAndTransportOptions } from '../../transport/useTransportAndTransportOptions';

import { DeliveryLocation } from './DeliveryLocation/DeliveryLocation';
import { LOCATION_FIELDS } from './LocationSection.const';
import LocalTexts from './LocationSection.texts';
import type { LocationFieldData } from './LocationSection.types';
import { SwitchableMap } from './SwitchableMap';

import styles from './LocationSection.module.scss';

const LocalT = new MDText(LocalTexts);

export const LocationSection: React.FC = () => {
	const {
		id: enquiryId,
		location,
		transport: { availableFrom, availableUntil },
	} = useVehicleData();
	const user: User = useUser();

	const markerLocations = {
		...location,
		dealer: {
			latitude: user.latitude,
			longitude: user.longitude,
		},
	};

	const isEstimatedDeliveryCostsEnabled = useFeatureToggle(FEATURES.estimatedDeliveryCosts);

	const { data, isLoading, shouldFetchTransportOptions } = useTransportAndTransportOptions();

	const tableData: LocationFieldData = {
		availability: {
			availableFrom,
			availableUntil,
		},
		deliveryLocation: {
			postcode: user?.postcode ?? '',
		},
		...(data ? { transportOptions: data.transportOptions } : { transportOptions: undefined }),
	};

	const distance = location?.seller?.distance || 0;

	if (isLoading && shouldFetchTransportOptions) {
		return (
			<div className={styles.loadingContainer}>
				<LoadingIndicator showText size="medium" text={LocalT.translate('loading') as string} />
			</div>
		);
	}

	return (
		<>
			<SwitchableMap
				distance={distance}
				enquiryId={enquiryId}
				isDealerMarkerVisible={isEstimatedDeliveryCostsEnabled}
				locations={markerLocations}
			/>
			<div className={styles.content}>
				<DeliveryLocation seller={location.seller} shouldShowEstimatedCost={isEstimatedDeliveryCostsEnabled} />
				<div className={styles.tableContent}>
					<SpecSection data={tableData} fields={LOCATION_FIELDS} />
				</div>
			</div>
		</>
	);
};
